import React from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    List,
    ListItem,
    ListItemText,
    ButtonGroup,
    IconButton,
    Tooltip,
    Divider,
    Chip,
} from "@mui/material";

// Components
import Icon from "@/theme/vectors/bundle";
import { TextField } from "@/theme/inputs/textfields";
import SlidePanel from "@/theme/partials/slidepanel";

const AttributesPanel = ({
    isOpen,
    onClose,
    tempAttributes,
    newAttribute,
    editingAttributeIndex,
    unsavedAttributeChanges,
    onAddAttribute,
    onEditAttribute,
    onRemoveAttribute,
    onCancelEdit,
    onAttributeInputChange,
    onSaveAttributes,
    saving,
    isViewMode = false,
    t,
}) => {
    return (
        <SlidePanel
            title={
                isViewMode
                    ? t("product.detail.attributes", "View Attributes")
                    : t("product.detail.attributes")
            }
            isOpen={isOpen}
            onClose={onClose}
            width="500px"
        >
            <Box sx={{ p: 3 }}>
                {!isViewMode && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            onAddAttribute();
                        }}
                    >
                        <TextField
                            label={t("product.detail.attributeName")}
                            name="name"
                            fullWidth
                            value={newAttribute.name}
                            onChange={onAttributeInputChange}
                            required
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            label={t("product.detail.attributeValues")}
                            name="values"
                            required
                            fullWidth
                            variant="outlined"
                            value={newAttribute.values}
                            onChange={onAttributeInputChange}
                            helperText={t("product.detail.attributeValuesHint")}
                            sx={{ mb: 3 }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                            >
                                {editingAttributeIndex >= 0
                                    ? t("product.detail.editAttribute")
                                    : t("product.detail.addAttribute")}
                            </Button>
                        </Box>
                    </form>
                )}

                {/* Attribute List */}
                {tempAttributes && tempAttributes.length > 0 && (
                    <Box sx={{ mt: isViewMode ? 0 : 4 }}>
                        {!isViewMode && <Divider sx={{ my: 2 }} />}
                        <Typography variant="h6" gutterBottom>
                            {t("product.detail.attributes")}
                        </Typography>
                        <List sx={{ mt: 2 }}>
                            {tempAttributes.map((attribute, index) => (
                                <Paper key={index} sx={{ mb: 2 }}>
                                    <ListItem
                                        sx={{
                                            py: 2,
                                            borderLeft: "4px solid",
                                            borderColor: "primary.main",
                                        }}
                                        secondaryAction={
                                            !isViewMode && (
                                                <ButtonGroup size="small">
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            onClick={() =>
                                                                onEditAttribute(
                                                                    index
                                                                )
                                                            }
                                                            size="small"
                                                        >
                                                            <Icon
                                                                name="edit"
                                                                size={18}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            onClick={() =>
                                                                onRemoveAttribute(
                                                                    index
                                                                )
                                                            }
                                                            color="error"
                                                            size="small"
                                                        >
                                                            <Icon
                                                                name="remove"
                                                                size={18}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </ButtonGroup>
                                            )
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                >
                                                    {attribute.name}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        gap: 1,
                                                        mt: 1,
                                                    }}
                                                >
                                                    {Array.isArray(
                                                        attribute.values
                                                    ) &&
                                                        attribute.values.map(
                                                            (
                                                                value,
                                                                valueIndex
                                                            ) => (
                                                                <Chip
                                                                    key={
                                                                        valueIndex
                                                                    }
                                                                    label={
                                                                        typeof value ===
                                                                            "object" &&
                                                                        value !==
                                                                            null
                                                                            ? value.value
                                                                            : value
                                                                    }
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            )
                                                        )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                </Paper>
                            ))}
                        </List>

                        {/* Save attributes button */}
                        {!isViewMode && unsavedAttributeChanges && (
                            <Box
                                sx={{
                                    mt: 4,
                                    display: "flex",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onSaveAttributes}
                                    startIcon={<Icon name="save" />}
                                >
                                    {t(
                                        "common.saveAttributes",
                                        "Save Attributes"
                                    )}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </SlidePanel>
    );
};

export default AttributesPanel;
